import {
  BIG_SHELF_MINIMUM_HEIGHT,
  CornerType,
  CrossPieceType,
  DoorHandleType,
  DrawerHandleType,
  Material,
  PaneMaterial,
  PlateType,
  Position,
  PositionAngle,
  THICKNESS_PLATE_DEFAULT,
  WHITE_COLOR,
} from '../constants';
import { CustomPiece } from '../pieces/CustomPiece';
import { Plate } from '../pieces/Plate';
import { Color } from '../entities/Color';
import { Door } from '../pieces/Door';
import { Drawer } from '../pieces/Drawer';
import { getPiecesData, setColors } from '../utils/dataStorage';
import colorsData from './fixtures/colors.json';
import { Pane } from '../pieces/Pane';
import { CrossPiece } from '../pieces/CrossPiece';
import { IntermediateShelf } from '../pieces/IntermediateShelf';
import { SupportIntermediateShelf } from '../pieces/SupportIntermediateShelf';
import { DoorHandle } from '../pieces/DoorHandle';
import { Corner } from '../pieces/Corner';
import { DrawerHandle } from '../pieces/DrawerHandle';
import { DrawerSlat } from '../pieces/DrawerSlat';
import { Rod } from '../pieces/Rod';
import { Batten } from '../pieces/Batten';

const colors = setColors(Color.setupColors(colorsData));
const whiteColor = colors.find(item => item.reference === WHITE_COLOR);

export function cornerFactory(
  position: PositionAngle = PositionAngle.FrontLeft,
  height = 1000,
  color: Color = whiteColor as Color,
  type: CornerType = CornerType.Normal,
): CustomPiece<Corner> {
  return new CustomPiece<Corner>(
    new Corner(
      1,
      '',
      'Corner',
      1,
      1,
      true,
      true,
      height,
      color,
      CornerType.Normal,
      undefined,
    ),
    true,
    position,
    height,
  );
}

export function intermediateShelFactory(
  height = 270,
  width = 1000,
  depth = 420,
  color: Color = whiteColor as Color,
): CustomPiece<IntermediateShelf> {
  return new CustomPiece<IntermediateShelf>(
    new IntermediateShelf(
      1,
      '',
      'TI',
      1,
      1,
      true,
      true,
      height,
      width,
      depth,
      color,
    ),
    false,
    0,
    undefined,
    undefined,
    undefined,
  );
}

export function supportIntermediateShelFactory(
  position: Position = Position.Left,
  height = 270,
  depth = 420,
  color: Color = whiteColor as Color,
): CustomPiece<SupportIntermediateShelf> {
  return new CustomPiece<SupportIntermediateShelf>(
    new SupportIntermediateShelf(
      1,
      '',
      'TI',
      1,
      1,
      true,
      true,
      height,
      depth,
      color,
    ),
    false,
    position,
    undefined,
    undefined,
    undefined,
  );
}

export function plateFactory(
  position: Position = Position.Front,
  height = 420,
  width = 1000,
): CustomPiece<Plate> {
  return Plate.standardPlate(height, width, position);
}

export function battenFactory(
  position: PositionAngle = PositionAngle.FrontLeft,
  height = 370,
  material: Material = Material.Wood,
  feet = false,
): CustomPiece<Batten> {
  return new CustomPiece<Batten>(
    new Batten(1, '', 'Batten', 1, 1, true, true, 40, material, feet),
    false,
    position,
  );
}

export function crossPieceFactory(
  position: Position = Position.Front,
  width = 1000,
  type: CrossPieceType = CrossPieceType.UpperPaneLowerPane,
  material: Material = Material.Wood,
  reinforce = false,
): CustomPiece<CrossPiece> {
  return new CustomPiece<CrossPiece>(
    new CrossPiece(
      1,
      '',
      'CP',
      1,
      1,
      true,
      true,
      40,
      width,
      type,
      material,
      reinforce,
      false,
    ),
    false,
    position,
  );
}

export function paneFactory(
  position: Position = Position.Front,
  height = 370,
  width = 1000,
  material: PaneMaterial = PaneMaterial.MDF,
): CustomPiece<Pane> {
  return new CustomPiece<Pane>(
    new Pane(
      1,
      '',
      'CP',
      1,
      1,
      true,
      true,
      height,
      width,
      material,
      whiteColor as Color,
    ),
    false,
    position,
  );
}

export function doorHandleFactory(
  type: DoorHandleType = DoorHandleType.Knop,
  color: Color = whiteColor as Color,
): DoorHandle {
  if (type === DoorHandleType.Knop)
    return new DoorHandle(3, '', 'CB', 0.51, 1, true, true, 0, type, color);
  else return new DoorHandle(3, '', 'CB', 1, 1, true, true, 0, type);
}

export function doorFactory(
  position: PositionAngle = PositionAngle.FrontRight,
  height = 370,
  width = 1000,
  material: PaneMaterial = PaneMaterial.MDF,
  hole = 38,
  color: Color = whiteColor as Color,
): CustomPiece<Door> {
  return new CustomPiece<Door>(
    new Door(
      1,
      '',
      'CP',
      1,
      1,
      true,
      true,
      height,
      width,
      material,
      hole,
      color,
    ),
    false,
    position,
    0,
    0,
    0,
    doorHandleFactory(DoorHandleType.Knop, color),
  );
}

export function doorCustomFactory(
  position: PositionAngle = PositionAngle.FrontRight,
  height = 345,
  width = 832,
  material: PaneMaterial = PaneMaterial.MDF,
  hole = 38,
  color: Color = whiteColor as Color,
): CustomPiece<Door> {
  return new CustomPiece<Door>(
    new Door(1, '', 'CP', 1, 1, true, true, 370, 800, material, hole, color),
    true,
    position,
    height,
    width,
    0,
    doorHandleFactory(DoorHandleType.Knop, color),
  );
}

export function drawerHandleFactory(
  type: DrawerHandleType = DrawerHandleType.Knop,
  color: Color = whiteColor as Color,
  position: PositionAngle | Position = PositionAngle.FrontLeft,
): CustomPiece<DrawerHandle> {
  if (type !== DrawerHandleType.Slat)
    return new CustomPiece<DrawerHandle>(
      new DrawerHandle(1, '', 'CP', 1, 1, true, true, 0, type, color),
      false,
      position,
    );
  return new CustomPiece<DrawerHandle>(
    new DrawerHandle(1, '', 'CP', 1, 1, true, true, 0, type),
    false,
    Position.Front,
  );
}

export function drawerSlatFactory(width: number): CustomPiece<DrawerSlat> {
  return new CustomPiece<DrawerSlat>(
    new DrawerSlat(
      1,
      `Slat ${width}mm`,
      `DRL-BH${width}`,
      1,
      1,
      true,
      true,
      0,
      width,
    ),
    false,
    Position.Front,
  );
}

export function drawerFactory(
  height = 270,
  width = 1000,
  depth = 420,
  color: Color = whiteColor as Color,
): CustomPiece<Drawer> {
  let handles = [];
  if (width >= BIG_SHELF_MINIMUM_HEIGHT) {
    handles = [
      drawerHandleFactory(
        DrawerHandleType.Knop,
        color,
        PositionAngle.FrontLeft,
      ),
      drawerHandleFactory(
        DrawerHandleType.Knop,
        color,
        PositionAngle.FrontRight,
      ),
    ];
  } else {
    handles = [
      drawerHandleFactory(DrawerHandleType.Knop, color, Position.Front),
    ];
  }
  return new CustomPiece<Drawer>(
    new Drawer(1, '', 'Drawer', 1, 1, true, true, height, width, depth, color),
    false,
    0,
    undefined,
    undefined,
    undefined,
    handles,
  );
}

export function rodFactory(width: number): CustomPiece<Rod> {
  return new CustomPiece<Rod>(
    new Rod(1, `Rod ${width}mm`, `Rod ${width}mm`, 1, 1, true, true, 1, width),
    false,
    Position.Front,
  );
}
